import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Card, CardSubtitleHeader } from "@/components/Card";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { Icon } from "@/components/Icon";
import { MenuItem } from "@/components/MenuItem";

import { ChangePasswordModal } from "./components/ChangePasswordModal";
import { DeleteAccountModal } from "./components/DeleteAccountModal";
import { ManageCalendarsModal } from "./components/ManageCalendarsModal";
import { ManageEmailAddressesModal } from "./components/ManageEmailAddressesModal";
import { ManageSessionsModal } from "./components/ManageSessionsModal";

export function SettingsPage() {
  const { t } = useTranslation();
  const changePasswordModal = useRef<HTMLDialogElement>(null);
  const manageCalendarsModal = useRef<HTMLDialogElement>(null);
  const manageSessionsModal = useRef<HTMLDialogElement>(null);
  const manageEmailAddressesModal = useRef<HTMLDialogElement>(null);
  const deleteAccountModal = useRef<HTMLDialogElement>(null);

  return (
    <FullScreenLayout>
      <ContentLayout wet title={<Link to="/settings">{t("settings-title")}</Link>}>
        <ContentFull className="flex flex-col gap-4 pt-28">
          <Card>
            <CardSubtitleHeader>{t("account-settings-title")}</CardSubtitleHeader>
            <div className="flex flex-col">
              <MenuItem onClick={() => manageEmailAddressesModal.current?.showModal()}>
                <Icon name="mail" />
                {t("manage-email-addresses")}
              </MenuItem>
              <MenuItem onClick={() => changePasswordModal.current?.showModal()}>
                <Icon name="key" />
                {t("change-password")}
              </MenuItem>
              <MenuItem disabled>
                <Icon name="shield" />
                {t("manage-2fa")}
                <div className="ml-auto">{t("feature-coming-soon")}</div>
              </MenuItem>
              <MenuItem onClick={() => manageSessionsModal.current?.showModal()}>
                <Icon name="monitor" />
                {t("manage-sessions")}
              </MenuItem>
              <MenuItem href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}`}>
                <Icon name="help-circle" />
                {t("get-help", { email: import.meta.env.VITE_SUPPORT_EMAIL })}
              </MenuItem>
            </div>
          </Card>

          <ManageEmailAddressesModal
            ref={manageEmailAddressesModal}
            onClose={() => manageEmailAddressesModal.current?.close()}
          />
          <ManageSessionsModal ref={manageSessionsModal} onClose={() => manageSessionsModal.current?.close()} />
          <ChangePasswordModal ref={changePasswordModal} onClose={() => changePasswordModal.current?.close()} />

          <Card>
            <CardSubtitleHeader>{t("notification-settings-title")}</CardSubtitleHeader>
            <div className="flex flex-col">
              <MenuItem onClick={() => manageCalendarsModal.current?.showModal()}>
                <Icon name="calendar" />
                {t("subscribe-to-calendar")}
              </MenuItem>
            </div>
          </Card>

          <ManageCalendarsModal ref={manageCalendarsModal} onClose={() => manageCalendarsModal.current?.close()} />

          <Card>
            <CardSubtitleHeader>{t("danger-zone-title")}</CardSubtitleHeader>
            <div className="flex flex-col">
              <MenuItem attention="danger" onClick={() => deleteAccountModal.current?.showModal()}>
                <Icon name="trash" />
                {t("delete-account")}
              </MenuItem>
            </div>
          </Card>

          <DeleteAccountModal ref={deleteAccountModal} onClose={() => deleteAccountModal.current?.close()} />
        </ContentFull>
      </ContentLayout>
    </FullScreenLayout>
  );
}
